import * as React from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { AudioLines, LogOut, Settings as SettingsIcon, User as UserIcon } from 'lucide-react';
import { useAuth } from '@/auth/auth-context';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/sonner';
import { cn } from '@/lib/utils';

const navLinkClass = ({ isActive }: { isActive: boolean }): string =>
  cn(
    'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
    isActive ? 'bg-secondary text-secondary-foreground' : 'text-muted-foreground hover:text-foreground',
  );

export function AppShell(): React.JSX.Element {
  const { user, logout, can } = useAuth();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = React.useState(false);

  const handleLogout = async (): Promise<void> => {
    setSigningOut(true);
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not sign out');
    } finally {
      setSigningOut(false);
    }
  };

  const canSeeSettings = can('settings.view');

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-[1600px] items-center gap-6 px-4">
          <Link to="/" className="flex items-center gap-2 font-semibold">
            <AudioLines className="h-5 w-5" />
            <span>TekVoice</span>
          </Link>

          <nav className="flex items-center gap-1">
            <NavLink to="/" end className={navLinkClass}>
              Batches
            </NavLink>
            {canSeeSettings ? (
              <NavLink to="/settings" className={navLinkClass}>
                Settings
              </NavLink>
            ) : null}
          </nav>

          <div className="ml-auto flex items-center gap-3">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2">
                    <UserIcon className="h-4 w-4" />
                    {user.name}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="flex flex-col gap-1">
                    <span>{user.email}</span>
                    <Badge variant="secondary" className="w-fit">
                      {user.role === 'SUPER_ADMIN' ? 'Super admin' : 'Employee'}
                    </Badge>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {canSeeSettings ? (
                    <DropdownMenuItem onSelect={() => navigate('/settings')}>
                      <SettingsIcon className="h-4 w-4" />
                      Settings
                    </DropdownMenuItem>
                  ) : null}
                  <DropdownMenuItem disabled={signingOut} onSelect={() => void handleLogout()}>
                    <LogOut className="h-4 w-4" />
                    {signingOut ? 'Signing out…' : 'Sign out'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1600px] px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}
